import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Trash2, Sparkles, RefreshCw, AlertCircle, ChevronDown, ChevronRight, Tag } from 'lucide-react';
import {
  getBuckets,
  getCategories,
  setSplitsApi,
  taxAllocationPreview,
  extractLineItems,
  createSkuLabel,
} from '../../lib/api';
import { toneFor, confidenceColors, useTheme } from '../../lib/theme';
import { groupByBucket } from '../../lib/categoryGrouping';

// ---------- helpers ----------
const rid = () => Math.random().toString(36).slice(2);
const emptySplit = () => ({ id: rid(), category: '', amount: '', line_item_ids: [] });
const round2 = (n) => Math.round(Number(n) * 100) / 100;

function ConfidenceMini({ confidence }) {
  const { effective } = useTheme();
  const tone = toneFor(confidence);
  const { bg, fg } = confidenceColors(tone, effective === 'dark');
  return (
    <span
      data-testid="line-item-confidence"
      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums"
      style={{ backgroundColor: bg, color: fg }}>
      {Math.round(confidence * 100)}%
    </span>
  );
}

// ---------- Category picker inline dropdown ----------
function CategoryPickerModal({ categories, buckets, colors, onPick, onClose, count, subtotal }) {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();
  const filtered = query
    ? categories.filter((c) => c.name.toLowerCase().includes(query))
    : categories;
  const grouped = groupByBucket(filtered, buckets);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl overflow-hidden max-h-[70vh] flex flex-col"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        data-testid="category-picker-modal">
        <div className="px-4 py-3 border-b" style={{ borderColor: colors.border }}>
          <p className="text-sm font-bold" style={{ color: colors.text }}>
            Assign {count} item{count === 1 ? '' : 's'} · ${subtotal.toFixed(2)}
          </p>
          <input autoFocus
            data-testid="category-picker-search"
            placeholder="Search category…"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full mt-2 px-3 py-2 rounded-lg text-sm outline-none"
            style={{ backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }} />
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {grouped.length === 0 ? (
            <p className="text-xs p-4 text-center" style={{ color: colors.textMuted }}>No categories</p>
          ) : (
            grouped.map((row) => (
              <div key={row.bucket} className="mb-2">
                <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 sticky top-0"
                  style={{ backgroundColor: colors.surface }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: row.color }} />
                  <span className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: colors.textMuted }}
                  >{row.label}</span>
                </div>
                {row.categories.map((c) => (
                  <button key={c.id}
                    data-testid={`category-picker-option-${c.name}`}
                    onClick={() => onPick(c.name)}
                    className="w-full text-left px-3 py-1.5 rounded-lg text-sm hover:opacity-80"
                    style={{ color: colors.text }}>
                    {c.name}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Line-Item row ----------
function LineItemRow({ item, checked, onToggle, onLabelSaved, colors }) {
  const [label, setLabel] = useState(item.description || '');
  const [savingLabel, setSavingLabel] = useState(false);
  const [labelErr, setLabelErr] = useState(null);

  const isLowConf = (item.confidence ?? 0) < 0.6;
  const isUnrec = !!item.is_unrecognized;

  const saveLabel = async () => {
    const trimmed = label.trim();
    if (!trimmed || !item.code) return;
    if (trimmed === (item.description || '')) return;
    setSavingLabel(true); setLabelErr(null);
    try {
      await createSkuLabel(item.code, trimmed, null, null);
      onLabelSaved(item.id, trimmed);
    } catch (e) {
      setLabelErr(e?.detail || 'Save failed');
    } finally {
      setSavingLabel(false);
    }
  };

  return (
    <div
      data-testid={`line-item-row-${item.id}`}
      className="flex items-center gap-2 py-2 pl-2 pr-3 rounded-lg"
      style={{
        backgroundColor: checked ? colors.accentSoft : 'transparent',
        borderLeft: isLowConf ? `3px solid ${colors.warning}` : '3px solid transparent',
      }}>
      <input type="checkbox" checked={checked}
        data-testid={`line-item-check-${item.id}`}
        onChange={() => onToggle(item.id)}
        className="cursor-pointer w-4 h-4 accent-current"
        style={{ accentColor: colors.accent }} />

      <div className="flex-1 min-w-0">
        {isUnrec ? (
          <div className="flex items-center gap-1.5">
            <span
              data-testid={`line-item-unrec-marker-${item.id}`}
              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: colors.error }} />
            <input
              data-testid={`line-item-label-input-${item.id}`}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={saveLabel}
              placeholder={item.code ? `Label ${item.code} (e.g. Coffee)` : 'Label this item'}
              disabled={savingLabel}
              className="flex-1 px-2 py-1 rounded text-sm outline-none min-w-0"
              style={{
                backgroundColor: colors.surface,
                color: colors.text,
                border: `1px solid ${colors.error}`,
                opacity: savingLabel ? 0.5 : 1,
              }} />
            {item.code ? (
              <span className="text-[10px] tabular-nums flex-shrink-0" style={{ color: colors.textMuted }}>
                #{item.code}
              </span>
            ) : null}
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className="text-sm truncate" style={{ color: colors.text }}>
              {item.description || (item.code ? `SKU ${item.code}` : 'Unnamed item')}
            </span>
            {item.code && item.description ? (
              <span className="text-[10px] tabular-nums" style={{ color: colors.textMuted }}>#{item.code}</span>
            ) : null}
          </div>
        )}
        {labelErr ? (
          <p className="text-[10px] mt-0.5" style={{ color: colors.error }}>{labelErr}</p>
        ) : null}
      </div>

      <ConfidenceMini confidence={item.confidence ?? 0} />
      <span className="text-xs tabular-nums w-20 text-right" style={{ color: colors.text }}>
        ${(item.subtotal ?? 0).toFixed(2)}
      </span>
    </div>
  );
}

// ---------- Main modal ----------
export default function SplitEditorModal({ receipt, colors, onClose, onSaved }) {
  const hasImage = (receipt.image_count || 0) > 0;

  const [tab, setTab] = useState(hasImage ? 'items' : 'manual');
  const [categories, setCategories] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [rows, setRows] = useState(
    receipt.splits?.length
      ? receipt.splits.map((s) => ({
          id: s.id, category: s.category, amount: String(s.amount),
          line_item_ids: s.line_item_ids || [],
        }))
      : [emptySplit(), emptySplit()],
  );
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Line-item state
  const [lineItems, setLineItems] = useState(receipt.line_items || []);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [splitsOpen, setSplitsOpen] = useState(true);

  useEffect(() => {
    getCategories()
      .then((r) => setCategories(r.categories.filter((c) => !c.is_special)))
      .catch(() => setCategories([]));
    getBuckets().then(setBuckets).catch(() => setBuckets([]));
  }, []);

  const receiptTotal = Number(receipt.total?.value || receipt.total || 0);

  const allocated = useMemo(() => {
    if (!preview) return null;
    let t = 0;
    preview.splits.forEach((s) => {
      t += Number(s.amount || 0) + Number(s.tax_gst || 0) + Number(s.tax_qst || 0) + Number(s.tax_hst || 0) + Number(s.tax_pst || 0);
    });
    return round2(t);
  }, [preview]);

  const doExtract = async () => {
    if (!hasImage) return;
    setExtracting(true); setExtractError(null);
    try {
      const r = await extractLineItems(receipt.id);
      setLineItems(r.line_items || []);
      setSelected(new Set());
    } catch (e) {
      setExtractError(e?.detail || 'Extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectedItems = lineItems.filter((li) => selected.has(li.id));
  const selectedSubtotal = round2(selectedItems.reduce((s, x) => s + Number(x.subtotal || 0), 0));

  const onLabelSaved = (itemId, newLabel) => {
    setLineItems((prev) => prev.map((it) => it.id === itemId ? { ...it, description: newLabel, is_unrecognized: false } : it));
  };

  const assignSelectedToCategory = (categoryName) => {
    if (selectedItems.length === 0) return;
    const ids = selectedItems.map((x) => x.id);
    // Remove these ids from any existing rows first, then create/update.
    setRows((prev) => {
      const cleaned = prev
        .map((r) => ({ ...r, line_item_ids: (r.line_item_ids || []).filter((x) => !ids.includes(x)) }))
        // Recompute amount from remaining line items if this row had line_item_ids
        .map((r) => {
          if (!r.line_item_ids) return r;
          const remaining = lineItems.filter((li) => r.line_item_ids.includes(li.id));
          if (remaining.length === 0 && (r.line_item_ids || []).length === 0 && r.amount === '') return r;
          if (remaining.length > 0) {
            const sum = round2(remaining.reduce((s, x) => s + Number(x.subtotal || 0), 0));
            return { ...r, amount: String(sum) };
          }
          return r;
        });
      // Find existing row with same category to merge
      const existingIdx = cleaned.findIndex((r) => r.category === categoryName);
      if (existingIdx >= 0) {
        const merged = { ...cleaned[existingIdx] };
        merged.line_item_ids = [...(merged.line_item_ids || []), ...ids];
        const items = lineItems.filter((li) => merged.line_item_ids.includes(li.id));
        merged.amount = String(round2(items.reduce((s, x) => s + Number(x.subtotal || 0), 0)));
        cleaned[existingIdx] = merged;
      } else {
        // Replace the first empty row, else append
        const emptyIdx = cleaned.findIndex((r) => !r.category && !r.amount);
        const newRow = {
          id: rid(), category: categoryName,
          amount: String(selectedSubtotal), line_item_ids: ids,
        };
        if (emptyIdx >= 0) cleaned[emptyIdx] = newRow;
        else cleaned.push(newRow);
      }
      return cleaned.filter((r) => r.category || r.amount || (r.line_item_ids?.length ?? 0) > 0);
    });
    clearSelection();
    setPickerOpen(false);
    setPreview(null);
    setSplitsOpen(true);
  };

  const validRows = rows.filter((r) => r.category && Number(r.amount) >= 0);
  const canPreview = validRows.length >= 1 && rows.every((r) => !r.category || Number(r.amount) >= 0);

  const recompute = async () => {
    if (!canPreview) return;
    setBusy(true); setError(null);
    try {
      const p = await taxAllocationPreview(
        receipt.id,
        validRows.map((r) => ({ category: r.category, amount: Number(r.amount) })),
      );
      setPreview(p);
    } catch (e) { setError(e?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!preview) { await recompute(); return; }
    setSaving(true); setError(null);
    try {
      // Attach line_item_ids from local rows back onto preview.splits (matched by category)
      const enriched = preview.splits.map((ps) => {
        const local = validRows.find((r) => r.category === ps.category);
        return { ...ps, line_item_ids: local?.line_item_ids || [] };
      });
      await setSplitsApi(receipt.id, enriched);
      onSaved();
    } catch (e) { setError(e?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const autoFill = () => {
    const others = rows.slice(0, -1).reduce((s, r) => s + Number(r.amount || 0), 0);
    const remaining = round2(receiptTotal - others);
    setRows((prev) => prev.map((r, i) => i === prev.length - 1
      ? { ...r, amount: String(Math.max(0, remaining)) } : r));
    setPreview(null);
  };

  const removeSplit = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setPreview(null);
  };

  const showComputeBtn = validRows.length >= 2;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl overflow-hidden max-h-[92vh] flex flex-col"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
        data-testid="split-editor-modal">

        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: colors.border }}>
          <div>
            <h2 className="text-lg font-bold" style={{ color: colors.text }}>Split by category</h2>
            <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
              {receipt.vendor?.value ?? '—'} · Total ${receiptTotal.toFixed(2)}
            </p>
          </div>
          <button onClick={onClose} data-testid="split-close-btn" style={{ color: colors.textMuted }}>
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: colors.border }}>
          <button
            data-testid="split-tab-items"
            onClick={() => setTab('items')}
            className="flex-1 py-2.5 text-sm font-semibold"
            style={{
              color: tab === 'items' ? colors.accent : colors.textMuted,
              borderBottom: tab === 'items' ? `2px solid ${colors.accent}` : '2px solid transparent',
            }}>
            From line items
          </button>
          <button
            data-testid="split-tab-manual"
            onClick={() => setTab('manual')}
            className="flex-1 py-2.5 text-sm font-semibold"
            style={{
              color: tab === 'manual' ? colors.accent : colors.textMuted,
              borderBottom: tab === 'manual' ? `2px solid ${colors.accent}` : '2px solid transparent',
            }}>
            Manual
          </button>
        </div>

        {/* Sticky selection bar (items tab only) */}
        {tab === 'items' && selected.size > 0 ? (
          <div className="px-5 py-2.5 flex items-center justify-between border-b"
            style={{ backgroundColor: colors.accentSoft, borderColor: colors.border }}
            data-testid="selection-bar">
            <p className="text-xs font-semibold" style={{ color: colors.accent }}>
              {selected.size} selected · ${selectedSubtotal.toFixed(2)}
            </p>
            <div className="flex gap-2">
              <button onClick={clearSelection}
                className="text-xs font-semibold px-2 py-1"
                style={{ color: colors.textMuted }}>Clear</button>
              <button
                data-testid="assign-selected-btn"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg text-white"
                style={{ backgroundColor: colors.accent }}>
                <Tag size={12} /> Assign to category
              </button>
            </div>
          </div>
        ) : null}

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'items' ? (
            <div className="p-5 space-y-3">
              {!hasImage ? (
                <div className="p-4 rounded-lg text-center" style={{ backgroundColor: colors.warningSoft }}>
                  <AlertCircle size={20} style={{ color: colors.warning, margin: '0 auto 6px' }} />
                  <p className="text-sm font-semibold" style={{ color: colors.warning }}>No image available</p>
                  <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                    Switch to <button className="underline" onClick={() => setTab('manual')}>Manual split</button> to enter amounts by category.
                  </p>
                </div>
              ) : lineItems.length === 0 && !extracting ? (
                <div className="text-center py-6">
                  <Sparkles size={26} style={{ color: colors.accent, margin: '0 auto 8px' }} />
                  <p className="text-sm font-semibold mb-1" style={{ color: colors.text }}>Extract line items from receipt image</p>
                  <p className="text-xs mb-4" style={{ color: colors.textMuted }}>
                    We&apos;ll read the receipt and list every purchased item so you can assign each one to a category.
                  </p>
                  <button
                    data-testid="extract-line-items-btn"
                    onClick={doExtract}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white"
                    style={{ backgroundColor: colors.accent }}>
                    <Sparkles size={14} /> Extract line items
                  </button>
                  {extractError ? (
                    <p className="text-xs mt-3" style={{ color: colors.error }}>{extractError}</p>
                  ) : null}
                </div>
              ) : extracting ? (
                <div className="text-center py-8" data-testid="extract-loading">
                  <div className="inline-block w-6 h-6 rounded-full animate-spin"
                    style={{ border: `3px solid ${colors.border}`, borderTopColor: colors.accent }} />
                  <p className="text-xs mt-3" style={{ color: colors.textMuted }}>Reading receipt with AI…</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold" style={{ color: colors.textMuted }}>
                      {lineItems.length} item{lineItems.length === 1 ? '' : 's'}
                    </p>
                    <button
                      data-testid="re-extract-btn"
                      onClick={doExtract}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1"
                      style={{ color: colors.textMuted }}>
                      <RefreshCw size={11} /> Re-extract
                    </button>
                  </div>
                  {extractError ? (
                    <div className="p-2 rounded-lg text-xs mb-2 flex items-center gap-2"
                      style={{ backgroundColor: colors.errorSoft, color: colors.error }}>
                      <AlertCircle size={12} /> {extractError}
                    </div>
                  ) : null}
                  <div data-testid="line-items-list">
                    {lineItems.map((it) => (
                      <LineItemRow key={it.id}
                        item={it}
                        checked={selected.has(it.id)}
                        onToggle={toggle}
                        onLabelSaved={onLabelSaved}
                        colors={colors} />
                    ))}
                  </div>
                </>
              )}

              {/* Current splits collapsible */}
              {rows.some((r) => r.category || Number(r.amount) > 0) ? (
                <div className="mt-4 pt-3 border-t" style={{ borderColor: colors.border }}>
                  <button onClick={() => setSplitsOpen((v) => !v)}
                    data-testid="toggle-current-splits"
                    className="w-full flex items-center gap-1 text-xs font-semibold py-1"
                    style={{ color: colors.text }}>
                    {splitsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Current splits ({rows.filter((r) => r.category).length})
                  </button>
                  {splitsOpen ? (
                    <div className="mt-2 space-y-1.5">
                      {rows.map((r, idx) => r.category || Number(r.amount) > 0 ? (
                        <div key={r.id}
                          data-testid={`current-split-row-${idx}`}
                          className="flex items-center gap-2 p-2 rounded-lg"
                          style={{ backgroundColor: colors.bg }}>
                          <span className="flex-1 text-sm font-semibold" style={{ color: colors.text }}>
                            {r.category || '(unassigned)'}
                          </span>
                          {(r.line_item_ids?.length ?? 0) > 0 ? (
                            <span className="text-[10px]" style={{ color: colors.textMuted }}>
                              {r.line_item_ids.length} item{r.line_item_ids.length === 1 ? '' : 's'}
                            </span>
                          ) : null}
                          <span className="text-sm tabular-nums" style={{ color: colors.text }}>
                            ${Number(r.amount || 0).toFixed(2)}
                          </span>
                          <button onClick={() => removeSplit(idx)}
                            data-testid={`remove-split-${idx}`}
                            style={{ color: colors.error }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ) : null)}
                      {showComputeBtn ? (
                        <button
                          data-testid="compute-tax-btn"
                          onClick={recompute}
                          disabled={busy}
                          className="mt-2 w-full inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-xs font-bold text-white"
                          style={{ backgroundColor: colors.accent, opacity: busy ? 0.5 : 1 }}>
                          {busy ? 'Computing…' : 'Compute tax allocation'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : (
            // ---- Manual tab (kept from prior implementation) ----
            <div className="p-5 space-y-3">
              {rows.map((r, idx) => (
                <div key={r.id} data-testid={`split-row-${idx}`}
                  className="flex items-center gap-2 p-3 rounded-lg border"
                  style={{ borderColor: colors.border, backgroundColor: colors.bg }}>
                  <select value={r.category}
                    data-testid={`split-cat-${idx}`}
                    onChange={(e) => {
                      setRows((prev) => prev.map((x, i) => i === idx ? { ...x, category: e.target.value } : x));
                      setPreview(null);
                    }}
                    className="flex-1 px-2 py-1.5 rounded text-sm outline-none"
                    style={{ backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }}>
                    <option value="">— category —</option>
                    {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                  <input type="number" step="0.01" value={r.amount}
                    data-testid={`split-amt-${idx}`}
                    onChange={(e) => {
                      setRows((prev) => prev.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x));
                      setPreview(null);
                    }}
                    placeholder="0.00"
                    className="w-28 px-2 py-1.5 rounded text-sm text-right outline-none tabular-nums"
                    style={{ backgroundColor: colors.surface, color: colors.text, border: `1px solid ${colors.border}` }} />
                  <button onClick={() => removeSplit(idx)}
                    data-testid={`split-del-${idx}`}
                    style={{ color: colors.error }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}

              <div className="flex gap-2 pt-1 flex-wrap">
                <button data-testid="add-split-btn"
                  onClick={() => setRows((prev) => [...prev, emptySplit()])}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border"
                  style={{ borderColor: colors.border, color: colors.text }}>
                  <Plus size={12} /> Add split
                </button>
                <button data-testid="autofill-btn"
                  onClick={autoFill}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border"
                  style={{ borderColor: colors.border, color: colors.text }}>
                  Auto-fill remainder
                </button>
                <button data-testid="preview-btn"
                  onClick={recompute}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold text-white"
                  style={{ backgroundColor: colors.accent, opacity: (!canPreview || busy) ? 0.5 : 1 }}
                  disabled={!canPreview || busy}>
                  Compute tax allocation
                </button>
              </div>
            </div>
          )}

          {/* Preview / error strip (shared, at bottom of body) */}
          <div className="px-5 pb-5">
            {error ? <p className="text-xs mt-2" style={{ color: colors.error }} data-testid="split-error">{error}</p> : null}

            {preview ? (
              <div className="p-3 rounded-lg mt-3" style={{ backgroundColor: colors.accentSoft }} data-testid="tax-allocation-preview">
                <p className="text-xs font-bold" style={{ color: colors.accent }}>
                  Allocated: ${allocated?.toFixed(2)} / ${receiptTotal.toFixed(2)}
                  {preview.reconciles ? ' ✓' : ' ⚠'}
                </p>
                {preview.warning ? <p className="text-[11px] mt-1" style={{ color: colors.warning }}>{preview.warning}</p> : null}
                <div className="mt-2 space-y-1">
                  {preview.splits.map((s, i) => (
                    <p key={i} className="text-[11px] tabular-nums" style={{ color: colors.textMuted }}>
                      {s.category}: ${s.amount.toFixed(2)}
                      {' + GST '}${s.tax_gst.toFixed(2)}
                      {' + QST '}${s.tax_qst.toFixed(2)}
                    </p>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex gap-2" style={{ borderColor: colors.border }}>
          <button onClick={onClose} className="flex-1 py-2 rounded-lg text-sm font-semibold border"
            style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.textMuted }}>
            Cancel
          </button>
          <button data-testid="save-splits-btn"
            onClick={save} disabled={!preview || !preview.reconciles || saving}
            className="flex-1 py-2 rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: colors.accent, opacity: (!preview || !preview.reconciles || saving) ? 0.5 : 1 }}>
            {saving ? '…' : 'Save splits'}
          </button>
        </div>
      </div>

      {pickerOpen ? (
        <CategoryPickerModal
          categories={categories}
          buckets={buckets}
          colors={colors}
          count={selected.size}
          subtotal={selectedSubtotal}
          onPick={assignSelectedToCategory}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
}
